/**
 * Redis pub/sub implementation of RealtimeAdapter for the self-hosted tier.
 *
 * Architecture:
 *   - subscribe() → opens an SSE connection to /api/realtime/subscribe
 *     which internally subscribes to a Redis channel and streams events.
 *   - broadcast() → POST to /api/realtime/publish which does PUBLISH on Redis.
 *
 * This replaces Supabase Realtime for high-throughput scenarios (>100 msg/sec).
 * The interface is identical to SupabaseRealtimeAdapter so swapping is a
 * one-line change in src/lib/adapters/index.ts.
 *
 * For Upstash (cloud tier): set NEXT_PUBLIC_REALTIME_BACKEND=upstash and
 * provide UPSTASH_REDIS_REST_URL / UPSTASH_REDIS_REST_TOKEN — the same
 * /api/realtime/* routes handle both backends via the REDIS_URL env var.
 */

import type {
  RealtimeAdapter,
  RealtimeEvent,
  RealtimePayload,
  RealtimeSubscription,
} from '../interfaces';

const BASE_URL = () =>
  typeof window !== 'undefined' ?'' : (process.env.NEXT_PUBLIC_SITE_URL ??'http://localhost:3000');

function getToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('nexus_access_token');
}

export class RedisRealtimeAdapter implements RealtimeAdapter {
  subscribe<T = Record<string, unknown>>(
    channel: string,
    table: string,
    event: RealtimeEvent,
    callback: (payload: RealtimePayload<T>) => void
  ): RealtimeSubscription {
    const token = getToken();
    const params = new URLSearchParams({
      channel,
      table,
      event,
      ...(token ? { token } : {}),
    });

    const url = `${BASE_URL()}/api/realtime/subscribe?${params.toString()}`;
    let es: EventSource | null = null;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) return;
      es = new EventSource(url);

      es.onmessage = (msg) => {
        try {
          const raw = JSON.parse(msg.data);
          // Ignore keepalive pings
          if (raw.type === 'ping') return;
          callback({
            eventType: raw.event_type ?? event,
            table: raw.table ?? table,
            schema: raw.schema ?? 'public',
            new: raw.new ?? null,
            old: raw.old ?? null,
          });
        } catch {
          // Ignore malformed messages
        }
      };

      es.onerror = () => {
        es?.close();
        if (!closed) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();

    return {
      unsubscribe: () => {
        closed = true;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        es?.close();
      },
    };
  }

  async broadcast(
    channel: string,
    event: string,
    payload: Record<string, unknown>
  ): Promise<void> {
    const token = getToken();
    await fetch(`${BASE_URL()}/api/realtime/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ channel, event, payload }),
    });
  }
}
